import { Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { Editor } from '@tiptap/react';
import { useIsFirstRender } from 'hooks/useIsFirstRender';
import { useAppSelector } from 'modules/Editor/store';
import { selectIsAllFontsLoaded } from 'modules/Editor/store/pageSelector';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { findFirstFontFamily } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.helpers';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import { LinkWrapper, useEditor, useWidget } from 'widgets/sdk';

import { findFirstFontSize } from '../TextWidget.helpers';
import { DEFAULT_CURVATURE } from '../defaultTextData';
import { computeDiameterFromFontSize, generateCurvedText } from './CurvedText.helpers';

// OBS: currently only supports a single font family and font size
interface CurvedTextWidgetProps {
  editor: Editor;
  setCurvedTextRef?: (ref: HTMLDivElement | null) => void;
  isEditable: boolean;
  letterSpacing?: string;
  allowHyperlinkClick?: boolean;
  curvature?: { radius: number };
}

/**
 * Curved text wrapper - copies same style as .ProseMirror class from prosemirror.css stylesheet
 * Ensures that the curved text has the same font rendering as the editor across diff browserss
 */
const CurvedWrapper = styled(Box)`
  /* Ensure font kerning off for all browsers */
  font-kerning: none;
  -webkit-font-kerning: none;

  -webkit-font-variant-ligatures: none;
  font-variant-ligatures: none;
  -webkit-font-feature-settings: 'kern' 0, 'liga' 0;
  font-feature-settings: 'kern' 0, 'liga' 0;
  -webkit-font-smoothing: antialiased;
  font-synthesis: none;

  /* Emphasizes precision over rendering speed and legibility */
  text-rendering: geometricPrecision;
`;

export const CurvedTextWidget = ({
  editor,
  isEditable,
  allowHyperlinkClick,
  letterSpacing = '',
  setCurvedTextRef = () => {},
  curvature = DEFAULT_CURVATURE,
}: CurvedTextWidgetProps) => {
  const { renderType } = useWidget();
  const isExport = renderType === ReadOnlyRenderType.ExportStructureTreeList;

  // State to force recalculating the curved text when the editor changes
  const [forceUpdate, setForceUpdate] = useState<Date | null>(null);

  const { boundingBox } = useEditor();

  const isAllFontsLoaded = useAppSelector(selectIsAllFontsLoaded);

  const isFirstRender = useIsFirstRender();

  const fontFamily = findFirstFontFamily(editor);
  const fontSize = findFirstFontSize(editor);
  const fontSizePx = parseFloat(fontSize) || 16;
  const { radius = 30 } = curvature;

  // Direction of the curvature: 1 = curve down, -1 = curve up (u shape)
  // Note: negative direction not yet supported in editor - calculations are provided but bounding box needs to be fixed
  const direction = radius >= 0 ? 1 : -1;

  // Outer diameter of the curved text
  const diameter = useMemo(() => computeDiameterFromFontSize(radius, fontSizePx), [radius, fontSizePx]);

  // Text transformations (rotate, translate) applied to each character
  const { width, height, textTransformations } = useMemo(
    () => generateCurvedText(editor, { fontFamily, fontSizePx: fontSize, letterSpacing }, diameter, direction),

    // Include isAllFontsLoaded and forceUpdate in the dependencies to recalculate the curved text when the fonts are loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, fontFamily, fontSize, diameter, direction, isAllFontsLoaded, forceUpdate, letterSpacing],
  );

  /**
   * Add event listener to update the curved text preview when editor changes (i.e. from typing)
   */
  useEffect(() => {
    if (!editor) {
      return;
    }

    const handleUpdate = () => {
      setForceUpdate(new Date());
    };

    editor.on('update', handleUpdate);
    editor.on('transaction', handleUpdate);

    // Cleanup the listener when the component unmounts
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor]);

  // Update the bounding box when the curved text dimensions are computed on first render
  useEffect(() => {
    if (isFirstRender) {
      boundingBox.updateRect();
    }
  }, [width, height, isFirstRender, boundingBox]);

  if (!editor) return null;

  return (
    <CurvedWrapper
      opacity={isEditable ? 0.35 : 1}
      fontFamily={fontFamily}
      fontSize={fontSize}
      width={`${width}px`}
      height={`${height}px`}
      pointerEvents={allowHyperlinkClick ? 'auto' : 'none'}
      ref={setCurvedTextRef}
    >
      {textTransformations.map(({ char, transform, letterStyle }, index) => {
        const r = diameter / 2;
        const originY = direction === 1 ? r : -r + fontSizePx;
        return (
          <BoldStyleWrapper key={`${char}-${index}`} isBold={letterStyle?.isBold || false}>
            <ItalicStyleWrapper isItalic={letterStyle?.isItalic || false}>
              <LinkWrapper hyperlink={letterStyle?.hyperlink} shouldKeepLink={isExport}>
                <span
                  key={`${char}-${index}`}
                  style={{
                    position: 'absolute',
                    transformOrigin: `center ${originY}px`,
                    transform: transform,
                    left: '50%',
                    color: letterStyle?.color || '#000',
                  }}
                >
                  <UnderlineStyleWrapper isUnderline={letterStyle?.isUnderline || false}>{char}</UnderlineStyleWrapper>
                </span>
              </LinkWrapper>
            </ItalicStyleWrapper>
          </BoldStyleWrapper>
        );
      })}
    </CurvedWrapper>
  );
};

interface BoldStyleWrapperProps {
  isBold: boolean;
  children: ReactNode;
}

const BoldStyleWrapper = ({ isBold, children }: BoldStyleWrapperProps) => {
  return isBold ? <strong>{children}</strong> : <>{children}</>;
};

interface ItalicStyleWrapperProps {
  isItalic: boolean;
  children: ReactNode;
}

const ItalicStyleWrapper = ({ isItalic, children }: ItalicStyleWrapperProps) => {
  return isItalic ? <em>{children}</em> : <>{children}</>;
};

interface UnderlineStyleWrapperProps {
  isUnderline: boolean;
  children: ReactNode;
}

const UnderlineStyleWrapper = ({ isUnderline, children }: UnderlineStyleWrapperProps) => {
  return isUnderline ? <u>{children}</u> : <>{children}</>;
};
